import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  TextField,
  Typography,
  Input,
  Select,
  MenuItem,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import { generateEncryptedKeyBody } from "../../utils/crypto";

import { errorToast, successToast, warnToast } from "../../helpers";
import { UploadMedia } from "../../utils/uploadMedia";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useAddSubCategoryMutation,
  useLazyGetSubCategoryByIdQuery,
  useUpdateSubCategoryMutation,
} from "../../services/main";

const AddSubCategory = () => {
  const { _id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [addCategory] = useAddSubCategoryMutation();
  const [updateCategory] = useUpdateSubCategoryMutation();
  const [getCategoryDetails] = useLazyGetSubCategoryByIdQuery();

  const [profilePicture, setProfilePicture] = useState("");
  const [categoryData, setCategoryData] = useState<any>();
  const [imageId, setImageId] = useState(0);

  const [category, setCategory] = useState<string>("");

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    try {
      if (!file) {
        setProfilePicture("");
        // setImageError("Image is required");
        return;
      }

      const res = await UploadMedia(file);
      if (res?.status === 200) {
        const imageUrl = res?.data[0]?.media_url;
        setProfilePicture(imageUrl);
        setImageId(res?.data[0]?.id);
        successToast("Image uploaded successfully.");
      } else {
        errorToast(res?.message);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      errorToast("Error uploading image. Please try again.");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title_en: categoryData?.sub_category_name_en || "",
      title_ar: categoryData?.sub_category_name_ar || "",
      title_russia: categoryData?.sub_category_name_rs || "",
    },
    validationSchema: Yup.object({
      title_en: Yup.string()
        .required("This field is required")
        .max(40, "Maximum 40 characters are allowed")
        .min(3, "Minimum 3 characters are required"),
      title_ar: Yup.string()
        .required("This field is required")
        .max(40, "Maximum 40 characters are allowed")
        .min(3, "Minimum 3 characters are required"),
      title_russia: Yup.string()
        .required("This field is required")
        .max(40, "Maximum 40 characters are allowed")
        .min(3, "Minimum 3 characters are required"),
    }),
    onSubmit: async (values) => {
      if (!profilePicture) {
        warnToast("Please upload an image");
        return;
      }

      const body = {
        category: state?.id,
        sub_category_name_en: values?.title_en || "",
        sub_category_name_ar: values?.title_ar || "",
        sub_category_name_rs: values?.title_russia || "",
        sub_category_image: imageId,
      };
      if (!_id)
        try {
          const encryptedBody = generateEncryptedKeyBody(body) as any;
          const response = await addCategory(encryptedBody).unwrap();
          if (response.status === 200) {
            successToast(
              response.data.message || "Category Added Successfully"
            );
            navigate("/manage-categories/");
          } else {
            errorToast("error");
          }
        } catch (e: any) {
          errorToast(e?.data?.message);
        }
      else {
        try {
          const encryptedBody2 = generateEncryptedKeyBody(body) as any;
          const response = await updateCategory({
            id: _id,
            body: encryptedBody2,
          }).unwrap();
          if (response.status === 200) {
            successToast("Category Updated Successfully");
            navigate("/manage-categories/");
          } else {
            errorToast(response?.message);
          }
        } catch (e: any) {
          errorToast(e?.message);
        }
      }
    },
  });

  const getCategoryDetail = async (id: string) => {
    try {
      const res = await getCategoryDetails({ id: _id }).unwrap();
      if (res?.status === 200) {
        setCategoryData(res?.data);
        setProfilePicture(res?.data?.sub_category_image?.media_url);
        setImageId(res?.data?.sub_category_image?.id);
      }
    } catch (error: any) {
      console.log(error, "errror");
      errorToast(error?.data?.message || "");
    }
  };

  useEffect(() => {
    if (_id && _id !== "add") {
      getCategoryDetail(_id);
    }
  }, []);

  return (
    <>
      <div>
        {/* <Loader isLoad={isLoading} /> */}
        <div className="main_loyout">
          <div className="dashboard">
            <h1>{_id ? "Edit Sub-Category" : "Add Sub-Category"}</h1>
            <Button
              className="btn btn_primary"
              onClick={() => {
                navigate(`/manage-categories/sub-categories/${state?.id}`);
              }}
            >
              Back
            </Button>
          </div>
          <Card className="cards">
            <form onSubmit={formik.handleSubmit}>
              <CardContent sx={{ p: 1 }}>
                <Grid container spacing={2}>
                  {/* Image Upload Section */}
                  <Grid item xs={12}>
                    <Typography className="custom_label">Image</Typography>
                    {profilePicture ? (
                      <div className="upload_image_preview">
                        <CardMedia
                          component="img"
                          style={{ borderRadius: 0 }}
                          image={profilePicture}
                          alt="photo"
                        />
                        <CancelIcon
                          onClick={(e) => {
                            e.preventDefault();
                            setProfilePicture("");
                          }}
                        />
                      </div>
                    ) : (
                      <Box className="upload_image">
                        <label htmlFor="icon-button-file">
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file"
                            type="file"
                            inputProps={{
                              accept: "image/png,image/jpeg",
                            }}
                            onChange={handleImageUpload}
                          />
                          <Button component="span" className="upload_image_btn">
                            <img
                              style={{ borderRadius: 0 }}
                              src="/static/images/product1.png"
                              alt=""
                            />
                            <CameraAltIcon />
                          </Button>
                        </label>
                      </Box>
                    )}
                  </Grid>

                  {/* Category Name Fields */}

                  <Grid item xs={4}>
                    <Typography className="custom_label">
                      Name (English)
                    </Typography>
                    <TextField
                      hiddenLabel
                      type="text"
                      name="title_en"
                      id="title_en"
                      inputProps={{ maxLength: 40 }}
                      variant="outlined"
                      fullWidth
                      value={formik.values.title_en}
                      placeholder="Enter Name in English"
                      className="text_field"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.title_en &&
                        Boolean(formik.errors.title_en)
                      }
                      helperText={
                        formik.touched.title_en && formik.errors.title_en
                          ? formik.errors.title_en.toString()
                          : ""
                      }
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Typography className="custom_label">
                      Name (Arabic)
                    </Typography>
                    <TextField
                      hiddenLabel
                      type="text"
                      name="title_ar"
                      id="title_ar"
                      inputProps={{ maxLength: 40 }}
                      variant="outlined"
                      fullWidth
                      value={formik.values.title_ar}
                      placeholder="Enter Name in Arabic"
                      className="text_field"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.title_ar &&
                        Boolean(formik.errors.title_ar)
                      }
                      helperText={
                        formik.touched.title_ar && formik.errors.title_ar
                          ? formik.errors.title_ar.toString()
                          : ""
                      }
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Typography className="custom_label">
                      Name (Russian)
                    </Typography>
                    <TextField
                      hiddenLabel
                      type="text"
                      name="title_russia"
                      id="title_russia"
                      inputProps={{ maxLength: 40 }}
                      variant="outlined"
                      fullWidth
                      value={formik.values.title_russia}
                      placeholder="Enter Name in Russian"
                      className="text_field"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.title_russia &&
                        Boolean(formik.errors.title_russia)
                      }
                      helperText={
                        formik.touched.title_russia &&
                        formik.errors.title_russia
                          ? formik.errors.title_russia.toString()
                          : ""
                      }
                    />
                  </Grid>
                </Grid>

                <div className="form_btn">
                  <Button
                    size="large"
                    type="submit"
                    className="btn btn_primary"
                  >
                    Save
                  </Button>
                </div>
              </CardContent>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AddSubCategory;
